import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import { Upload as UploadIcon, Download as DownloadIcon } from '@mui/icons-material';
import apiClient from '../../api/client';
import { parseCsvToObjects, toCsv, downloadCsv } from '../../utils/csv';

/** Header spellings accepted in an uploaded file, normalised to our field names. */
const FIELD_ALIASES: Record<string, string> = {
  firstname: 'firstName',
  lastname: 'lastName',
  email: 'email',
  phone: 'phone',
  phonenumber: 'phone',
  role: 'role',
  password: 'password',
  employeeid: 'employeeId',
  address: 'address',
  notes: 'notes',
  licenseexpirydate: 'licenseExpiryDate',
  licenceexpirydate: 'licenseExpiryDate',
  dotcardexpirydate: 'dotCardExpiryDate',
  mecexpirationdate: 'mecExpirationDate',
};

const TEMPLATE_HEADERS = [
  'firstName', 'lastName', 'email', 'phone', 'role', 'password',
  'employeeId', 'address', 'notes',
  'licenseExpiryDate', 'dotCardExpiryDate', 'mecExpirationDate',
];

// Row 1 leaves password blank — that person gets the normal invitation email.
// Row 2 sets one, so the account is created active and nothing is sent.
const TEMPLATE_EXAMPLE = [
  ['Jane', 'Doe', 'jane.doe@example.com', '+15551230001', 'Driver', '', 'EMP-001', '1 Main St', '', '2027-01-31', '2026-11-30', '2027-06-15'],
  ['Sam', 'Ruiz', 'sam.ruiz@example.com', '+15551230002', 'Maintainer', 'Str0ng!Pass', '', '', '', '', '', ''],
];

/** The server caps a single request; larger files are sent in chunks. */
const CHUNK_SIZE = 50;

interface RowResult {
  row: number;
  email: string;
  name: string;
  ok: boolean;
  code: string;
  message: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

const BulkInviteDialog: React.FC<Props> = ({ open, onClose, onImported }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [unknownHeaders, setUnknownHeaders] = useState<string[]>([]);
  const [results, setResults] = useState<RowResult[] | null>(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [committed, setCommitted] = useState(false);

  const reset = () => {
    setFileName('');
    setRows([]);
    setUnknownHeaders([]);
    setResults(null);
    setChecked(false);
    setBusy(false);
    setProgress(0);
    setError('');
    setCommitted(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (busy) return;
    if (committed) onImported();
    reset();
    onClose();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setResults(null);
    setChecked(false);
    setCommitted(false);

    try {
      const text = await file.text();
      const parsed = parseCsvToObjects(text, FIELD_ALIASES);
      if (parsed.rows.length === 0) {
        setError('That file has no data rows. Download the template to see the expected columns.');
        return;
      }
      setFileName(file.name);
      setRows(parsed.rows);
      setUnknownHeaders(parsed.unknownHeaders);
    } catch (err: any) {
      setError(`Could not read that file: ${err.message}`);
    }
  };

  /** Send the file through the API in chunks, dry run or for real. */
  const send = async (dryRun: boolean) => {
    setBusy(true);
    setError('');
    setProgress(0);

    const all: RowResult[] = [];
    try {
      for (let start = 0; start < rows.length; start += CHUNK_SIZE) {
        const chunk = rows.slice(start, start + CHUNK_SIZE);
        const response: any = await apiClient.post('/api/invitations/bulk', { rows: chunk, dryRun });
        // Row indexes come back relative to the chunk; make them file-relative
        // so the numbers match what the admin sees in their spreadsheet.
        all.push(...(response.results || []).map((r: RowResult) => ({ ...r, row: r.row + start })));
        setProgress(Math.min(start + CHUNK_SIZE, rows.length));
      }
      setResults(all);
      if (dryRun) {
        setChecked(true);
      } else {
        setCommitted(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'The import failed. Nothing further was sent.');
      if (all.length) setResults(all);
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    downloadCsv('topops-invite-template.csv', toCsv(TEMPLATE_HEADERS, TEMPLATE_EXAMPLE));
  };

  const downloadFailures = () => {
    const failed = (results || []).filter((r) => !r.ok);
    const rowsOut = failed.map((r) => {
      const original = rows[r.row] || {};
      return [...TEMPLATE_HEADERS.map((h) => original[h] ?? ''), r.message];
    });
    downloadCsv('topops-invite-failures.csv', toCsv([...TEMPLATE_HEADERS, 'problem'], rowsOut));
  };

  const okCount = (results || []).filter((r) => r.ok).length;
  const failCount = (results || []).length - okCount;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import team from a CSV</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!rows.length && (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Required columns: <strong>firstName, lastName, email, phone, role</strong>. Optional:
              password, employeeId, address, notes, licenseExpiryDate, dotCardExpiryDate,
              mecExpirationDate. Dates in YYYY-MM-DD.
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Leave <strong>password</strong> blank to send the usual invitation email and let the
              person choose their own. Fill it in and the account is created ready to sign in with
              no email sent — you pass the password on yourself. Someone who already has a TopoSDS
              account keeps their existing password either way.
            </Typography>
            <Link component="button" type="button" onClick={downloadTemplate} sx={{ mb: 3, display: 'inline-block' }}>
              Download the template
            </Link>
            <Box>
              <Button variant="contained" component="label" startIcon={<UploadIcon />}>
                Choose CSV file
                <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleFile} />
              </Button>
            </Box>
          </Box>
        )}

        {rows.length > 0 && (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" gutterBottom>
              <strong>{fileName}</strong> — {rows.length} row{rows.length === 1 ? '' : 's'}
            </Typography>

            {unknownHeaders.length > 0 && (
              <Alert severity="warning" sx={{ my: 2 }}>
                These columns were not recognised and will be ignored:{' '}
                {unknownHeaders.join(', ')}
              </Alert>
            )}

            {!checked && !committed && (
              <Alert severity="info" sx={{ my: 2 }}>
                Nothing is sent yet. Check the file first — every row is validated against your
                company before a single invitation goes out.
              </Alert>
            )}

            {busy && (
              <Box sx={{ my: 2 }}>
                <LinearProgress variant="determinate" value={(progress / rows.length) * 100} />
                <Typography variant="caption" color="text.secondary">
                  {progress} of {rows.length}
                </Typography>
              </Box>
            )}

            {results && (
              <>
                <Box sx={{ display: 'flex', gap: 1, my: 2, alignItems: 'center' }}>
                  <Chip
                    label={`${okCount} ${committed ? 'invited' : 'ready'}`}
                    color="success"
                    size="small"
                  />
                  {failCount > 0 && <Chip label={`${failCount} with problems`} color="error" size="small" />}
                  {failCount > 0 && (
                    <Button size="small" startIcon={<DownloadIcon />} onClick={downloadFailures}>
                      Download the failed rows
                    </Button>
                  )}
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.map((r) => (
                        <TableRow key={`${r.row}-${r.email}`}>
                          {/* +2: one for the header line, one because
                              spreadsheets count from 1. */}
                          <TableCell>{r.row + 2}</TableCell>
                          <TableCell>{r.name}</TableCell>
                          <TableCell>{r.email}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color={r.ok ? 'success.main' : 'error.main'}>
                              {r.message}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {committed && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Done. Invited people appear in the list as <strong>pending</strong> until they
                accept; you can resend or revoke from there.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          {committed ? 'Close' : 'Cancel'}
        </Button>
        {rows.length > 0 && !committed && (
          <>
            <Button onClick={() => send(true)} disabled={busy}>
              {busy && !checked ? <CircularProgress size={20} /> : 'Check file'}
            </Button>
            <Button
              variant="contained"
              onClick={() => send(false)}
              disabled={busy || !checked || okCount === 0}
            >
              {busy && checked ? <CircularProgress size={20} /> : `Send ${okCount} invitation${okCount === 1 ? '' : 's'}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkInviteDialog;
